export default class WorkflowUtils {
  static validateAction(action) {
    if (!action?.Type) return false;

    if (action?.Type === "record-create-update") {
      if (!action?.ModuleAPIName) return false;

      if (action?.FieldMapping?.length < 1) return false;

      if (typeof action?.Match !== "boolean") return false;

      if (action?.Match && !action.MatchFields.length) return false;

      if (action?.Match && !["update", "skip"].includes(action?.MatchMethod)) {
        return false;
      }

      for (let fieldMap of action?.FieldMapping) {
        if (!fieldMap?.Source) return false;

        if (fieldMap?.Source === "File" && !fieldMap?.SourceKey) return false;

        if (
          (fieldMap?.Source === "Value" || fieldMap?.Source === "Dynamic") &&
          !fieldMap.Value
        )
          return false;

        if (fieldMap?.Source === "Action" && !fieldMap?.ActionID) return false;
      }
    }

    if (action?.Type === "record-create-update-subitems") {
      if (!action?.ModuleAPIName) return false;

      if (action?.FieldMapping?.length < 1) return false;

      if (typeof action?.Match !== "boolean") return false;

      if (action?.Match && !action.MatchFields.length) return false;

      if (action?.Match && !["update", "skip"].includes(action?.MatchMethod)) {
        return false;
      }
    }

    return true;
  }

  static validateWorkflow(workflow) {
    let result = true;
    let messages = [];

    if (!workflow?.Name) {
      result = false;
      messages.push("A name is required for Workflows.");
    }

    if (
      !workflow?.Trigger ||
      !workflow?.Trigger.hasOwnProperty("Type") ||
      !workflow.Trigger.Type
    ) {
      result = false;
      messages.push("A trigger is required for Workflows.");
    }

    if (!Array.isArray(workflow?.Actions) || !workflow?.Actions?.length) {
      result = false;
      messages.push("At least one action is required for Workflows.");
    }

    return {
      accepted: result,
      message: messages.length
        ? messages.join("\r\n")
        : "Workflow passed validation.",
    };
  }

  static checkForReferenceErrors(a, payload) {
    const available = payload.AvailableActions;
    const actionIds = payload.ActionIds;
    const refAction = a?.ReferencedAction;

    if (!refAction) {
      payload.Errors[a.id] = ["No reference action provided"];
    }

    if (!available[a.id].includes(refAction) && actionIds.includes(refAction)) {
      payload.Errors[a.id] = [
        "Referenced an action that occurs after this action.",
      ];
    }

    if (
      !available[a.id].includes(refAction) &&
      !actionIds.includes(refAction)
    ) {
      payload.Errors[a.id] = ["The referenced action no longer exists."];
    }
  }

  // Handles the traversal of  Workflow Actions.
  // Payload determines what update you want to make to the workflow tree.
  static traverseActionsAndUpdate(actions, payload) {
    actions = [...actions];
    const nestedArrays = [actions];
    const parentIndexes = [null];

    const checkedIds = [];
    let i = 0;

    while (nestedArrays.length) {
      const a = nestedArrays[0][i];
      const parentIndex = parentIndexes[0];
      let currentIndex;

      // Prevent infinite loop in case logic is erroneous
      if (a?.id && checkedIds.includes(a?.id)) {
        console.error(
          "Error in 'traverseActionsAndUpdate' - Same ID encountered"
        );
        return actions;
      }

      a?.id && checkedIds.push(a?.id);

      // ------------------- Payload Actions ---------------------
      if (payload?.Type === "ReturnIndex") {
        if (!parentIndex) {
          currentIndex = i + 1;
        } else {
          const decimal = (i + 1) / 10;
          currentIndex = parentIndex + decimal;
        }
      }

      if (
        payload?.Type === "CheckReferences" &&
        payload.ReferenceTypes.includes(a.Type)
      ) {
        this.checkForReferenceErrors(a, payload);
      }

      if (a?.id === payload?.id) {
        if (payload.Type === "Delete") {
          nestedArrays[0].splice(i, 1);
          return actions;
        }
        if (payload.Type === "Update") {
          nestedArrays[0][i] = payload.updatedAction;
          return actions;
        }
        if (payload.Type === "RemoveAndReturn") {
          const removedAction = nestedArrays[0].splice(i, 1)[0];
          return { removedAction, actions };
        }
        if (payload.Type === "Return") {
          return { action: a, actions };
        }
        if (payload.Type === "Add") {
          nestedArrays[0].splice(i, 0, payload.addAction);
          return actions;
        }
        if (payload.Type === "AddAfter") {
          nestedArrays[0].splice(i + 1, 0, payload.addAction);
          return actions;
        }
        if (payload.Type === "ReturnIndex") {
          return currentIndex;
        }
        if (payload.Type === "AddToNested") {
          const { NestedSide, NewAction } = payload;

          a[`${NestedSide}Actions`] = [...a[`${NestedSide}Actions`], NewAction];
          return actions;
        }
      }
      // ----------------------- End of Payload Actions ------------------------

      if (a?.TrueActions && a.TrueActions?.length > 0) {
        a.TrueActions = [...a.TrueActions];
        nestedArrays.push(a.TrueActions);
        parentIndexes.push(i + 1);
      }

      if (a?.FalseActions && a.FalseActions?.length > 0) {
        a.FalseActions = [...a.FalseActions];
        nestedArrays.push(a.FalseActions);
        parentIndexes.push(i + 1);
      }

      i++;

      if (i === nestedArrays[0].length) {
        i = 0;
        nestedArrays.shift();
        parentIndexes.shift();
      }
    }

    if (payload?.Type === "ReturnActionIds") {
      return checkedIds;
    }

    if (payload?.Type === "CheckReferences") {
      return payload.Errors;
    }

    return actions;
  }

  /**  Used to diagnose a workflow actions tree
       Returns erroneous actions with an error message.

       Possible Errors:
       1. For Actions that need a Referenced Action
          1.a. The referenced action doesn't occur before the action.
          1.b. The referenced action no longers exists.
          1.c. There is no referenced action entirely.
       
  */
  static diagnoseWorkflow(actions) {
    if (!actions || actions?.length === 0) return null;

    // The actions available to reference for each action.
    const availableActions = this.recurseActions(actions);

    const allActionsIds = this.traverseActionsAndUpdate(actions, {
      Type: "ReturnActionIds",
    });

    // Errors will be the result returned the end. Actions IDs are the keys, and the value is an array of error messages.
    const errors = this.traverseActionsAndUpdate(actions, {
      Type: "CheckReferences",
      AvailableActions: availableActions,
      ActionIds: allActionsIds,
      ReferenceTypes: [
        "if-else-case",
        "record-update-fields",
        "record-update-tags",
      ],
      Errors: {},
    });

    // Return null if there are no erroneous actions.
    return Object.keys(errors).length ? errors : null;
  }

  static recurseActions(actions, available = [], result = {}) {
    let i = 0;

    while (i < actions.length) {
      const a = actions[i];

      if (result.hasOwnProperty(a.id)) {
        return console.error("Same action ID encountered in recurseActions");
      }

      result[a.id] = [...available];

      if (a.Type === "record-create-update") {
        available.push(a.id);
      }

      if (a.Type === "if-else-case") {
        this.recurseActions(a?.TrueActions, [...available], result);
        this.recurseActions(a?.FalseActions, [...available], result);
      }

      i++;
    }

    return result;
  }
}
